
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SiteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteToEdit?: { id: string; name: string; organization_id?: string } | null;
}

export const SiteDialog = ({ open, onOpenChange, siteToEdit }: SiteDialogProps) => {
  const [name, setName] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    if (siteToEdit) {
      setName(siteToEdit.name);
      setOrganizationId(siteToEdit.organization_id || "");
    } else {
      setName("");
      setOrganizationId("");
    }
  }, [siteToEdit]);

  const { data: organizations } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select(`
          *,
          region:regions(name)
        `)
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (siteToEdit) {
        const { error } = await supabase
          .from("sites")
          .update({ name, organization_id: organizationId })
          .eq("id", siteToEdit.id);
        
        if (error) throw error;
        toast.success("Site updated successfully");
      } else {
        const { error } = await supabase
          .from("sites")
          .insert([{ name, organization_id: organizationId }]);
        
        if (error) throw error;
        toast.success("Site created successfully");
      }
      
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      onOpenChange(false);
      setName("");
      setOrganizationId("");
    } catch (error) {
      toast.error(siteToEdit ? "Failed to update site" : "Failed to create site");
      console.error("Error with site:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{siteToEdit ? "Edit Site" : "Add New Site"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Site Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter site name"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="organization">Organization</Label>
            <Select value={organizationId} onValueChange={setOrganizationId} required>
              <SelectTrigger>
                <SelectValue placeholder="Select an organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations?.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name} ({org.region?.name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit">{siteToEdit ? "Update Site" : "Add Site"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
